import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, CardBody, Row, Col, Button, Input, Label, FormGroup } from "reactstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { FaPlus, FaTrash } from "react-icons/fa";

const AddPartner = () => {
  const navigate = useNavigate();
  const { AuthReducer: { auth } } = useSelector((state) => state);

  const [formData, setFormData] = useState({
    businessName: "",
    fullName: "",
    category: "",
    email: "",
    phoneNumber: "",
    panNumber: "",
    city: "",
    state: "",
    description: "",
    type: "",
    websiteLink: "",
    appLink: "",
    addresses: [{ addressType: "Business", addressLine1: "", city: "", state: "", pincode: "" }],
    bankAccounts: [{ accountNumber: "", ifscCode: "", beneficiaryName: "" }],
    gstDetails: [{ gstNumber: "" }],
    contacts: [{ name: "", designation: "", phone: "", email: "" }],
    documents: []
  });

  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (arrayName, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addArrayItem = (arrayName, defaultItem) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: [...prev[arrayName], defaultItem]
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      documents: [...prev.documents, ...files.map(file => ({ file, docType: "" }))]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Add basic fields
      Object.keys(formData).forEach(key => {
        if (key !== 'documents' && key !== 'addresses' && key !== 'bankAccounts' && key !== 'gstDetails' && key !== 'contacts') {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add arrays as JSON
      formDataToSend.append('addresses', JSON.stringify(formData.addresses));
      formDataToSend.append('bankAccounts', JSON.stringify(formData.bankAccounts));
      formDataToSend.append('gstDetails', JSON.stringify(formData.gstDetails));
      formDataToSend.append('contacts', JSON.stringify(formData.contacts));

      // Add documents
      formData.documents.forEach((doc, index) => {
        if (doc.file) {
          formDataToSend.append(`documents`, doc.file);
          formDataToSend.append(`docTypes`, doc.docType);
        }
      });

      await axios.post("/api/admin/v1/add-partner", formDataToSend, {
        headers: { 
          Authorization: `Bearer ${auth?.token}`,
          'Content-Type': 'multipart/form-data'
        },
      });

      toast.success("Partner added successfully");
      navigate("/partners");
    } catch (error) {
      toast.error("Error adding partner");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="row my-3 align-items-center">
        <div className="col-md-10">
          <h4 className="text-white fw-bold">Add New Partner</h4>
        </div>
        <div className="col-md-2 text-md-end">
          <Button color="secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="bg-white px-3 py-4 rounded-3 shadow-sm my-4">
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card className="mb-4">
            <CardBody>
              <h6 className="text-primary fw-bold mb-3">Basic Information</h6>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Business Name *</Label>
                    <Input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Full Name *</Label>
                    <Input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Category *</Label>
                    <Input
                      type="select"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Category</option>
                      <option value="Hotel">Hotel</option>
                      <option value="Restaurant">Restaurant</option>
                      <option value="Transport">Transport</option>
                      <option value="Tour Guide">Tour Guide</option>
                      <option value="Activity Provider">Activity Provider</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Type</Label>
                    <Input
                      type="select"
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Type</option>
                      <option value="Individual">Individual</option>
                      <option value="Company">Company</option>
                      <option value="Partnership">Partnership</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Phone Number *</Label>
                    <Input
                      type="text"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>PAN Number</Label>
                    <Input
                      type="text"
                      name="panNumber"
                      value={formData.panNumber}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>City</Label>
                    <Input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>State</Label>
                    <Input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Website Link</Label>
                    <Input
                      type="url"
                      name="websiteLink"
                      value={formData.websiteLink}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <FormGroup>
                    <Label>App Link</Label>
                    <Input
                      type="url"
                      name="appLink"
                      value={formData.appLink}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <FormGroup>
                    <Label>Description</Label>
                    <Input
                      type="textarea"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Addresses */}
          <Card className="mb-4">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-primary fw-bold mb-0">Addresses</h6>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => addArrayItem('addresses', { addressType: "Business", addressLine1: "", city: "", state: "", pincode: "" })}
                >
                  <FaPlus /> Add Address
                </Button>
              </div>
              {formData.addresses.map((address, index) => (
                <div key={index} className="border p-3 mb-3 rounded">
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Address Type</Label>
                        <Input
                          type="select"
                          value={address.addressType}
                          onChange={(e) => handleArrayChange('addresses', index, 'addressType', e.target.value)}
                        >
                          <option value="Business">Business</option>
                          <option value="Registered">Registered</option>
                          <option value="Correspondence">Correspondence</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Address Line 1</Label>
                        <Input
                          type="text"
                          value={address.addressLine1}
                          onChange={(e) => handleArrayChange('addresses', index, 'addressLine1', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>City</Label>
                        <Input
                          type="text"
                          value={address.city}
                          onChange={(e) => handleArrayChange('addresses', index, 'city', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>State</Label>
                        <Input
                          type="text"
                          value={address.state}
                          onChange={(e) => handleArrayChange('addresses', index, 'state', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Pincode</Label>
                        <Input
                          type="text"
                          value={address.pincode}
                          onChange={(e) => handleArrayChange('addresses', index, 'pincode', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => removeArrayItem('addresses', index)}
                        disabled={formData.addresses.length === 1}
                      >
                        <FaTrash />
                      </Button>
                    </Col>
                  </Row>
                </div>
              ))}
            </CardBody>
          </Card>

          {/* Bank Accounts */}
          <Card className="mb-4">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-primary fw-bold mb-0">Bank Accounts</h6>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => addArrayItem('bankAccounts', { accountNumber: "", ifscCode: "", beneficiaryName: "" })}
                >
                  <FaPlus /> Add Bank Account
                </Button>
              </div>
              {formData.bankAccounts.map((bank, index) => (
                <div key={index} className="border p-3 mb-3 rounded">
                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Account Number</Label>
                        <Input
                          type="text"
                          value={bank.accountNumber}
                          onChange={(e) => handleArrayChange('bankAccounts', index, 'accountNumber', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>IFSC Code</Label>
                        <Input
                          type="text"
                          value={bank.ifscCode}
                          onChange={(e) => handleArrayChange('bankAccounts', index, 'ifscCode', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Beneficiary Name</Label>
                        <Input
                          type="text"
                          value={bank.beneficiaryName}
                          onChange={(e) => handleArrayChange('bankAccounts', index, 'beneficiaryName', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => removeArrayItem('bankAccounts', index)}
                        disabled={formData.bankAccounts.length === 1}
                      >
                        <FaTrash />
                      </Button>
                    </Col>
                  </Row>
                </div>
              ))}
            </CardBody>
          </Card>

          {/* GST Details */}
          <Card className="mb-4">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-primary fw-bold mb-0">GST Details</h6>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => addArrayItem('gstDetails', { gstNumber: "" })}
                >
                  <FaPlus /> Add GST
                </Button>
              </div>
              {formData.gstDetails.map((gst, index) => (
                <div key={index} className="border p-3 mb-3 rounded">
                  <Row>
                    <Col md={11}>
                      <FormGroup>
                        <Label>GST Number</Label>
                        <Input
                          type="text"
                          value={gst.gstNumber}
                          onChange={(e) => handleArrayChange('gstDetails', index, 'gstNumber', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => removeArrayItem('gstDetails', index)}
                        disabled={formData.gstDetails.length === 1}
                      >
                        <FaTrash />
                      </Button>
                    </Col>
                  </Row>
                </div>
              ))}
            </CardBody>
          </Card>

          {/* Contacts */}
          <Card className="mb-4">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-primary fw-bold mb-0">Contact Details</h6>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => addArrayItem('contacts', { name: "", designation: "", phone: "", email: "" })}
                >
                  <FaPlus /> Add Contact
                </Button>
              </div>
              {formData.contacts.map((contact, index) => (
                <div key={index} className="border p-3 mb-3 rounded">
                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Name</Label>
                        <Input
                          type="text"
                          value={contact.name}
                          onChange={(e) => handleArrayChange('contacts', index, 'name', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Designation</Label>
                        <Input
                          type="text"
                          value={contact.designation}
                          onChange={(e) => handleArrayChange('contacts', index, 'designation', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label>Phone</Label>
                        <Input
                          type="text"
                          value={contact.phone}
                          onChange={(e) => handleArrayChange('contacts', index, 'phone', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={contact.email}
                          onChange={(e) => handleArrayChange('contacts', index, 'email', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => removeArrayItem('contacts', index)}
                        disabled={formData.contacts.length === 1}
                      >
                        <FaTrash />
                      </Button>
                    </Col>
                  </Row>
                </div>
              ))}
            </CardBody>
          </Card>

          {/* Documents */}
          <Card className="mb-4">
            <CardBody>
              <h6 className="text-primary fw-bold mb-3">Documents</h6>
              <FormGroup>
                <Label>Upload Documents</Label>
                <Input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </FormGroup>
              {formData.documents.map((doc, index) => (
                <div key={index} className="border p-3 mb-3 rounded">
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Document Type</Label>
                        <Input
                          type="select"
                          value={doc.docType}
                          onChange={(e) => {
                            const newDocs = [...formData.documents];
                            newDocs[index].docType = e.target.value;
                            setFormData(prev => ({ ...prev, documents: newDocs }));
                          }}
                        >
                          <option value="">Select Document Type</option>
                          <option value="PAN Card">PAN Card</option>
                          <option value="Aadhar Card">Aadhar Card</option>
                          <option value="GST Certificate">GST Certificate</option>
                          <option value="Business License">Business License</option>
                          <option value="Bank Statement">Bank Statement</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={5}>
                      <p className="mt-2"><strong>File:</strong> {doc.file?.name}</p>
                    </Col>
                    <Col md={1} className="d-flex align-items-center">
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => {
                          const newDocs = formData.documents.filter((_, i) => i !== index);
                          setFormData(prev => ({ ...prev, documents: newDocs }));
                        }}
                      >
                        <FaTrash />
                      </Button>
                    </Col>
                  </Row>
                </div>
              ))}
            </CardBody>
          </Card>

          {/* Submit Button */}
          <div className="text-center">
            <Button
              type="submit"
              color="primary"
              size="lg"
              disabled={loading}
            >
              {loading ? "Adding Partner..." : "Add Partner"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPartner;